import {
  Accordion,
  ActiveFormatter,
  addWeeks,
  Attribute,
  Button,
  CustomMobileAccordionType,
  DataTable,
  DataTableColumn,
  DATE_FORMATTER_STR,
  DateRange,
  DateRangeInput,
  DateTimeFormatter,
  Filters,
  formatDate,
  FormatterType,
  GameInfo,
  Header,
  ScrollContainer,
  Select,
  SortFilters,
  StatsGameFormatter,
  StatsTeam,
  StickyFooterButtons,
  tableSort,
  TextFormatter,
  useBreakpoints,
  useLoading,
  ValueOpt,
} from "best-common-react";
import React, { useCallback, useEffect, useState } from "react";
import { exportDigitalTicketsReport, getDigitalTicketsRecords } from "../../../api/RequesTixApi";
import { urlReplace } from "../../../constants/RouteConstants";
import RouteConstants from "../../../constants/RouteConstants";
import { useDropdowns } from "../../../contexts/DropdownsContext";
import { DigitalTicketDeliveryDTO, DigitalTicketType } from "../../../types/DigitalTickets";
import { useUrlParams } from "../../../util/RouteUtil";
import { downloadLink, FileTypes } from "../../../util/FileUtil";
import IconLinkFormatter from "../../../components/tables/IconLinkFormatter";
const DigitalTicketDeliveryAccordion: React.FC<CustomMobileAccordionType<DigitalTicketDeliveryDTO>> = ({
  data,
  ...rest
}) => {
  const {
    requestId,
    game,
    to,
    status,
    active,
    seats,
    from,
    createdTs,
    ticketIds,
    failedTicketIds,
    errorMessage,
    forwardId,
    revokeId,
  } = data;

  return (
    <Accordion {...rest}>
      <Accordion.Header>
        <RequestLinkFormatter value={requestId} row={data} />
        <GameInfo
          gameDate={game!.gameDate}
          venue={game!.venue}
          home={game!.teams.home.team}
          away={game!.teams.away.team}
          seriesDescription={game!.seriesDescription}
        />
      </Accordion.Header>
      <Accordion.Body>
        <div>
          <Attribute header="To" value={to} />
          <Attribute header="Status" value={status} />
          <Attribute header="Active" value={<ActiveFormatter value={active} />} />
          <Attribute header="Seats" value={seats} />
          <Attribute header="From" value={from} />
          <Attribute header="Created" value={<DateTimeFormatter value={createdTs} />} />
          <Attribute header="Ticket IDs" value={<TicketIdsFormatter value={ticketIds} />} />
          <Attribute header="Failed Ticket IDs" value={<TicketIdsFormatter value={failedTicketIds} />} />
          <Attribute header="Error Msg" value={errorMessage} />
          <Attribute header="Forward ID" value={forwardId} />
          <Attribute header="Revoke ID" value={revokeId} />
        </div>
      </Accordion.Body>
    </Accordion>
  );
};

const RequestLinkFormatter: React.FC<FormatterType<DigitalTicketDeliveryDTO>> = ({ row, ...rest }) => {
  const { requestId, suiteRequestId, todaysTicketRequestId, type } = row!;
  let editLink;
  if (type === "Suite Request") {
    editLink = urlReplace(RouteConstants.ADMIN.SUITE_REQUESTS.EDIT, { suiteRequestId });
  } else if (type === "Ticket Request") {
    editLink = urlReplace(RouteConstants.ADMIN.REQUESTS_EDIT, { requestId });
  } else if (type === "Today's Ticket") {
    editLink = urlReplace(RouteConstants.ADMIN.TODAYS_TICKETS_EDIT, { requestId: todaysTicketRequestId });
  }

  if (!!editLink) {
    return <IconLinkFormatter {...rest} value={editLink} icon="fa-pencil-alt" />;
  } else {
    return <div />;
  }
};

const TicketIdsFormatter: React.FC<FormatterType<DigitalTicketDeliveryDTO>> = ({ value = [], ...rest }) => {
  return <TextFormatter {...rest} value={value?.join(", ")} />;
};

const Cols: DataTableColumn<DigitalTicketDeliveryDTO>[] = [
  { key: "requestId", name: "", width: 40, readonlyFormatter: RequestLinkFormatter, fixed: true },
  { key: "type", name: "Type", width: 150, sortable: true, fixed: true },
  {
    name: "Game",
    key: "game",
    readonlyFormatter: StatsGameFormatter,
    width: 325,
    fixed: true,
  },
  { key: "to", name: "To", width: 250, sortable: true },
  { key: "status", name: "Status", width: 100, sortable: true },
  { key: "active", name: "Active", width: 70, readonlyFormatter: ActiveFormatter, cellClass: "justify-content-center" },
  { key: "seats", name: "Seats", width: 250 },
  { key: "from", name: "From", width: 250, sortable: true },
  { key: "createdTs", name: "Sent Date", width: 250, readonlyFormatter: DateTimeFormatter },
  { key: "ticketIds", name: "Ticket IDs", minWidth: 300, readonlyFormatter: TicketIdsFormatter },
  { key: "failedTicketIds", name: "Failed Ticket IDs", width: 300, readonlyFormatter: TicketIdsFormatter },
  { key: "errorMessage", name: "Error Message", width: 100 },
  { key: "forwardId", name: "Forward ID", width: 350 },
  { key: "revokeId", name: "Revoke ID", width: 350 },
];

const today = new Date();
const previousWeek = addWeeks(today, -1);
const todayStr = formatDate(today, DATE_FORMATTER_STR);
const oneWeekAgoStr = formatDate(previousWeek, DATE_FORMATTER_STR);

const AdminDigitalTickets: React.FC = () => {
  const { setLoading } = useLoading();
  const { loading, setLoadingNonBlocking } = useLoading();
  const { teamsOptions, digitalTicketTypeOptions } = useDropdowns();
  const { isMobile } = useBreakpoints();
  const [tickets, setTickets] = useState<DigitalTicketDeliveryDTO[]>([]);
  const [displayTickets, setDisplayTickets] = useState<DigitalTicketDeliveryDTO[]>([]);
  const [sortFilters, setSortFilters] = useState<SortFilters<DigitalTicketDeliveryDTO>>({
    key: "createdTs",
    direction: "DESC",
  });

  const [{ startDate, endDate, teamId, type }, setParam] = useUrlParams({
    startDate: oneWeekAgoStr,
    endDate: todayStr,
    teamId: undefined,
    type: undefined,
  });

  const dateRange: DateRange<Date> = {
    start: new Date(Date.parse(startDate!)),
    end: new Date(Date.parse(endDate!)),
  };

  const updateDate = useCallback(
    ({ start, end }: DateRange<Date>) => {
      setParam({
        startDate: formatDate(start!, DATE_FORMATTER_STR),
        endDate: formatDate(end!, DATE_FORMATTER_STR),
      });
    },
    [setParam],
  );

  const updateType = useCallback(
    (newType: string | undefined) => {
      setParam("type", newType);
    },
    [setParam],
  );

  const updateTeamId = useCallback(
    (newTeamId: number | string | undefined) => {
      setParam("teamId", newTeamId !== undefined ? String(newTeamId) : undefined);
    },
    [setParam],
  );

  const fetchTickets = useCallback(async () => {
    try {
      setLoadingNonBlocking(true);
      const result: DigitalTicketDeliveryDTO[] = await getDigitalTicketsRecords(
        startDate,
        endDate,
        teamId ? Number(teamId) : undefined,
        type,
      );
      setTickets(result);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingNonBlocking(false);
    }
  }, [startDate, endDate, teamId, type, setLoadingNonBlocking]);

  const exportReport = async () => {
    try {
      setLoading(true);
      const response = await exportDigitalTicketsReport(startDate, endDate, teamId ? Number(teamId) : undefined, type);
      downloadLink(response, FileTypes.EXCEL);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    setDisplayTickets(tableSort(tickets, sortFilters));
  }, [tickets, sortFilters]);

  return (
    <div>
      <Header
        sticky
        title={
          <div className="d-flex justify-content-between align-items-center">
            <div>Digital Tickets Sent</div>
            <StickyFooterButtons startsOpen={true}>
              <Button variant="primary" onClick={exportReport}>
                Export
              </Button>
            </StickyFooterButtons>
          </div>
        }
        flexColumn
      >
        <Filters actionIconPosition="absolute" numberDisplayed={isMobile ? 1 : 3}>
          <DateRangeInput
            id="game-date"
            label="Game Date"
            value={dateRange}
            onChange={updateDate}
            maxRange={{
              weeks: 2,
            }}
            withPortal
            popoverPlacement="bottom-end"
          />
          <Select
            id="home-team-dropdown"
            label="Home Team"
            options={teamsOptions}
            value={teamsOptions.find((opt) => {
              if (teamId) {
                return opt.value.id === Number(teamId);
              } else {
                return !opt.value.id;
              }
            })}
            onChange={(team: ValueOpt<StatsTeam>) => updateTeamId(team?.value?.id)}
          />
          <Select
            id="type-dropdown"
            label="Type"
            options={digitalTicketTypeOptions}
            value={digitalTicketTypeOptions.find((opt: ValueOpt<DigitalTicketType>) => {
              if (!!type) {
                return opt.value === type;
              } else {
                return !opt.value;
              }
            })}
            onChange={(team: ValueOpt<DigitalTicketType>) => updateType(team?.value)}
          />
        </Filters>
      </Header>
      <ScrollContainer className="bcr-p-2">
        <DataTable
          className="bcr-mb-2"
          data={displayTickets}
          columns={Cols}
          loading={loading}
          sortColumn={sortFilters["key"]}
          sortDirection={sortFilters["direction"]}
          sortFunction={(key, direction) => setSortFilters({ key, direction })}
          accordion={DigitalTicketDeliveryAccordion}
        />
      </ScrollContainer>
    </div>
  );
};

export default AdminDigitalTickets;
